import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Users, Shield } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";
import { AccessDenied, canManagePersonnel, assignableRoles } from "@/lib/permissions";

const roleBadge = (r) => {
  const map = {
    owner:      "bg-primary/10 text-primary border-primary/20",
    manager:    "bg-blue-50 text-blue-700 border-blue-200",
    accountant: "bg-violet-50 text-violet-700 border-violet-200",
    staff:      "bg-muted text-muted-foreground border-transparent",
    kitchen:    "bg-amber-50 text-amber-700 border-amber-200",
  };
  return <Badge variant="outline" className={`${map[r] || map.staff} capitalize rounded-md font-medium`}>{r}</Badge>;
};

export default function Staff() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const denied = !canManagePersonnel(user);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState(false);
  const empty = { email: "", password: "", full_name: "", role: "staff" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const roleOptions = assignableRoles(user?.role);

  // Hotfix — hard block staff/kitchen/waiter/accountant AFTER hooks run.
  if (denied) return <AccessDenied testid="staff-access-denied" />;

  const validate = () => {
    const e = {};
    if (!form.full_name?.trim()) e.full_name = lang === "tr" ? "İsim zorunlu" : "Name erforderlich";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = lang === "tr" ? "Geçerli e-posta gir" : "Gültige E-Mail";
    if ((form.password || "").length < 6) e.password = lang === "tr" ? "En az 6 karakter" : "min. 6 Zeichen";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!validate()) return;
    try {
      await api.post("/staff", form);
      setOpen(false); setForm(empty); setErrors({}); setRefreshKey((k) => k + 1);
      toast.success(t("save"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/staff/${id}`);
      setRefreshKey((k) => k + 1);
      toast.success(t("delete"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const columns = [
    { key: "full_name", label: t("fullName"), className: "font-medium", render: (r) => (
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
          {(r.full_name || "?").slice(0, 1).toUpperCase()}
        </div>
        <span>{r.full_name}</span>
      </div>
    )},
    { key: "email", label: t("email"), className: "text-muted-foreground font-mono text-xs" },
    { key: "role", label: t("role"), render: (r) => roleBadge(r.role) },
    { key: "actions", label: "", className: "text-right w-10", render: (r) => (
      r.id !== user?.id ? (
        <ConfirmDeleteButton onConfirm={() => remove(r.id)} itemName={r.full_name || r.email} data-testid={`del-staff-${r.id}`} />
      ) : <Shield className="w-4 h-4 text-muted-foreground inline-block" title="You" />
    )},
  ];

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        title={t("staff")}
        description={lang === "tr" ? "Ekibini yönet — rol tabanlı erişim (Owner · Manager · Accountant · Staff)" : "Team-Verwaltung — rollenbasierter Zugriff"}
        action={{ label: t("add"), icon: Plus, onClick: () => { setForm(empty); setOpen(true); }, testid: "new-staff-btn" }}
      />

      <DataTable
        columns={columns}
        fetcher={async () => {
          const { data } = await api.get("/staff");
          return { items: data, total: data.length, page: 1, page_size: data.length };
        }}
        pageSize={100}
        refreshKey={refreshKey}
        rowTestId="staff-row"
        searchPlaceholder={lang === "tr" ? "İsim / e-posta ara…" : "Suchen…"}
        emptyProps={{ icon: Users, title: lang === "tr" ? "Kullanıcı yok" : "Kein Personal", description: lang === "tr" ? "Ekibine yeni bir kullanıcı davet et." : "Neuen Benutzer einladen.", action: { label: t("add"), icon: Plus, onClick: () => setOpen(true) } }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("add")} — {t("staff")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("fullName")}</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="staff-name" className="mt-1" />{errors.full_name && <p className="text-xs text-destructive mt-1">{errors.full_name}</p>}</div>
            <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" className="mt-1" />{errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}</div>
            <div><Label>{t("password")}</Label><Input type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-pw" className="mt-1" />{errors.password && <p className="text-xs text-destructive mt-1">{errors.password}</p>}</div>
            <div>
              <Label>{t("role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roleOptions.map(r => (
                    <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-staff">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
